import ReconnectingWebSocket from 'reconnecting-websocket';

import {
  normalizeWeexMessage,
  weexChangePercent,
  WeexDepthSequence,
  weexPong,
} from '@/shared/cex-sdk/exchanges/weex/protocol';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook, keepNearestOrderbookLevels, mergeOrderBook } from '../utils';

// WEEX has NO browser CORS (api-spot.weex.com returns no access-control-allow-origin),
// so every REST call MUST be routed through the proxy. We reuse `proxyAxios` (basic
// auth + proxy baseURL baked from env) and the same `/api/v1/proxy?url=<encoded target>`
// routing the shared SDK proxy client uses (src/shared/cex-sdk/lib/proxy-client.ts).
const WEEX_REST_BASE = 'https://api-spot.weex.com';
const WEEX_PROXY_PATH = '/api/v1/proxy';
const WEEX_WS_URL = 'wss://ws-spot.weex.com/v3/ws/public';

// WEEX v3 public WS depth channel only supports levels 15 and 200.
const WEEX_WS_DEPTH_LEVEL = 200;

// Build the full target URL with query params and route it through the proxy, mirroring
// exchangeProxyClient.get() in src/shared/cex-sdk/lib/proxy-client.ts.
const proxyGet = <T>(url: string, params: Record<string, string | number>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => search.append(key, String(value)));
  const query = search.toString();
  const targetUrl = query ? `${url}?${query}` : url;

  return proxyAxios.get<T>(WEEX_PROXY_PATH, { params: { url: targetUrl } });
};

// REST snapshot poll cadence (ms). Keeps panels populated even if the browser WS to
// WEEX is blocked/unsupported.
const WEEX_REST_POLL_INTERVAL = 5000;

// WEEX candle interval values (mirror shared/cex-sdk/exchanges/weex/timeframes.ts and
// connector/weex/common/enum.go): 1m,5m,15m,30m,1h,4h,12h,1d,1w,1M.
// The shared `cexExchangesTimeframes` table carries bitget-style values for WEEX, so we
// map the page interval to the correct WEEX period locally instead.
const WEEX_PERIOD_BY_TIMEFRAME: Partial<Record<IChartTimeframe, string>> = {
  '1s': '1m',
  '10s': '1m',
  '30s': '1m',
  '1m': '1m',
  '3m': '5m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '2h',
  '4h': '4h',
  '6h': '6h',
  '8h': '8h',
  '12h': '12h',
  '24h': '1d',
  '1d': '1d',
  '2d': '1d',
  '3d': '1d',
  '5d': '1d',
  '168h': '1w',
  '1w': '1w',
  '1M': '1M',
};

// WEEX kline WS channel period suffix per connectWebSocket interval (ms).
const WEEX_PERIOD_BY_INTERVAL_MS: Record<number, string> = {
  60000: '1m',
  300000: '5m',
  900000: '15m',
  1800000: '30m',
  3600000: '1h',
  7200000: '2h',
  14400000: '4h',
  21600000: '6h',
  28800000: '8h',
  43200000: '12h',
  86400000: '1d',
  604800000: '1w',
  2592000000: '1M',
};

// WEEX exchangeInfo returns a string `tickSize` (e.g. "0.01"), not an integer
// precision. Derive the decimal-place count so the orderbook aggregation tick logic
// (shared with bitget) works.
const precisionFromTickSize = (tickSize: string | undefined): number => {
  if (!tickSize) return 1;
  const num = Number(tickSize);
  if (!Number.isFinite(num) || num <= 0) return 1;
  const [, decimals = ''] = tickSize.split('.');
  const trimmed = decimals.replace(/0+$/, '');
  return trimmed.length || 0;
};

const toTuples = (levels: { price: string; size: string }[]): [string, string][] =>
  levels.map(level => [level.price, level.size]);

export class WeexStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;
  pollInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  weexSymbol: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;
  hasWsDepthSnapshot: boolean;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.weexSymbol = this.pair;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.hasWsDepthSnapshot = false;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await proxyGet<{
        symbols: { tickSize: string }[];
      }>(`${WEEX_REST_BASE}/api/v3/exchangeInfo`, { symbol: this.weexSymbol });

      let prec = 1;

      if (Array.isArray(res.data?.symbols)) {
        prec = precisionFromTickSize(res.data.symbols?.[0]?.tickSize);
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Weex ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const period = WEEX_PERIOD_BY_TIMEFRAME[interval] ?? '1m';
    const symbol = `${base}${quote}`.toUpperCase();

    try {
      // V3 candles use interval and return [openTime, OHLC, baseVolume, closeTime, quoteVolume].
      const result = await proxyGet<(string | number)[][]>(
        `${WEEX_REST_BASE}/api/v3/market/klines`,
        { symbol, interval: period },
      );

      // WEEX returns ms timestamps; the chart pipeline (handleNewCharts) already
      // converts ms->seconds, so return ms unchanged like every other strategy
      // (mexc/bitget). Pre-dividing here caused a DOUBLE /1000 -> first candle = 1970.
      return (Array.isArray(result.data) ? result.data : []).map(el => [
        Number(el[0]),
        String(el[1]),
        String(el[2]),
        String(el[3]),
        String(el[4]),
        String(el[5]),
      ]) as KLine[];
    } catch (error) {
      console.log('Weex klines error: ', error);

      return [];
    }
  };

  // Seed Last Trades immediately, including quiet markets with no new WS messages.
  // V3 uses an unsuffixed symbol and returns a bare array of public market trades.
  // https://www.weex.com/api-doc/spot/MarketDataAPI/GetTradeData
  fetchTrades = async ({
    base,
    quote,
    limit,
  }: {
    base: string;
    quote: string;
    limit: number;
  }): Promise<TradeSocket[]> => {
    const count = Number.isFinite(limit) ? Math.max(1, Math.min(1000, Math.floor(limit))) : 100;
    const response = await proxyGet<
      { price: string; qty: string; time: number; isBuyerMaker: boolean }[]
    >(`${WEEX_REST_BASE}/api/v3/market/trades`, {
      symbol: `${base}${quote}`.toUpperCase(),
      limit: count,
    });

    if (!Array.isArray(response.data)) {
      throw new Error('Invalid WEEX recent trades response');
    }

    return response.data
      .filter(
        trade =>
          trade != null &&
          Number.isFinite(Number(trade.price)) &&
          Number(trade.price) > 0 &&
          Number.isFinite(Number(trade.qty)) &&
          Number(trade.qty) > 0 &&
          Number.isFinite(Number(trade.time)) &&
          Number(trade.time) > 0 &&
          typeof trade.isBuyerMaker === 'boolean',
      )
      .sort((a, b) => Number(b.time) - Number(a.time))
      .slice(0, count)
      .map(trade => ({
        price: String(trade.price),
        quantity: String(trade.qty),
        timestamp: String(trade.time),
        side: trade.isBuyerMaker ? 'sell' : 'buy',
      }));
  };

  fetchDepthSnapshot = async () => {
    try {
      if (this.hasWsDepthSnapshot) return;

      // v3 returns a bare book; keep enough raw levels for client aggregation.
      const res = await proxyGet<{
        asks: [string, string][];
        bids: [string, string][];
      }>(`${WEEX_REST_BASE}/api/v3/market/depth`, {
        symbol: this.weexSymbol,
        limit: WEEX_WS_DEPTH_LEVEL,
      });

      if (this.hasWsDepthSnapshot) return;
      if (!Array.isArray(res.data?.asks) || !Array.isArray(res.data?.bids)) return;

      const responseAsks = res.data.asks ?? [];
      const responseBids = res.data.bids ?? [];

      const nearest = keepNearestOrderbookLevels(
        mergeOrderBook(responseAsks, []) as [string, string][],
        mergeOrderBook(responseBids, []) as [string, string][],
        WEEX_WS_DEPTH_LEVEL,
      );
      this.asks = nearest.asks;
      this.bids = nearest.bids;

      this.publishOrderbook();
    } catch (error) {
      console.log('Weex depth snapshot error: ', error);
    }
  };

  fetchTickerSnapshot = async () => {
    try {
      const res = await proxyGet<{
        lastPrice: string;
        openPrice: string;
        highPrice: string;
        lowPrice: string;
        volume: string;
        quoteVolume: string;
        priceChangePercent: string;
      }>(`${WEEX_REST_BASE}/api/v3/market/ticker/24hr`, { symbol: this.weexSymbol });
      if (!res.data?.lastPrice) return;
      const {
        lastPrice,
        highPrice: high,
        lowPrice: low,
        volume: size,
        quoteVolume: value,
        priceChangePercent,
      } = res.data;

      this.onTickerUpdate({
        baseVolume: size,
        change: normalizeNumber(
          Number(weexChangePercent(lastPrice, res.data.openPrice, priceChangePercent)),
        ),
        close: lastPrice,
        high,
        last: Number(lastPrice).toFixed(this.precision),
        low,
        quoteVolume: value,
      });
    } catch (error) {
      console.log('Weex ticker snapshot error: ', error);
    }
  };

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(
        this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.selectedPrecision,
        this.depth,
      ),
    );
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket(WEEX_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;
    const depthSequence = new WeexDepthSequence();

    const periodSuffix = WEEX_PERIOD_BY_INTERVAL_MS[interval] ?? '1m';
    const tickerChannel = `${this.weexSymbol}@ticker`;
    const depthChannel = `${this.weexSymbol}@depth${WEEX_WS_DEPTH_LEVEL}`;
    const tradesChannel = `${this.weexSymbol}@trade`;
    const klineChannel = `${this.weexSymbol}@kline_${periodSuffix}_LAST_PRICE`;

    // REST snapshots so the panels populate immediately and stay fresh even if the
    // browser WS to WEEX is blocked/unsupported.
    this.fetchDepthSnapshot();
    this.fetchTickerSnapshot();

    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    this.pollInterval = setInterval(() => {
      this.fetchDepthSnapshot();
      this.fetchTickerSnapshot();
    }, WEEX_REST_POLL_INTERVAL);

    this.ws.onopen = () => {
      this.hasWsDepthSnapshot = false;
      depthSequence.reset();
      ws.send(
        JSON.stringify({
          method: 'SUBSCRIBE',
          params: [tickerChannel, depthChannel, tradesChannel, klineChannel],
          id: 1,
        }),
      );
    };

    this.ws.onclose = () => {
      this.hasWsDepthSnapshot = false;
      depthSequence.reset();
    };

    this.ws.onmessage = event => {
      try {
        const wire = JSON.parse(event.data);
        const pong = weexPong(wire);
        if (pong) {
          ws.send(JSON.stringify(pong));
          return;
        }
        if (wire.s && wire.s !== this.weexSymbol) return;
        const continuity = depthSequence.accept(wire);
        if (continuity === 'gap') {
          this.hasWsDepthSnapshot = false;
          ws.reconnect();
          this.fetchDepthSnapshot();
        }
        if (continuity !== 'accept') return;
        const data = normalizeWeexMessage(wire, WEEX_WS_DEPTH_LEVEL);
        if (!data || wire.s !== this.weexSymbol) return;

        const channel = data?.channel as string | undefined;

        if (channel === tickerChannel && data?.data?.[0]) {
          const { lastPrice, high, low, size, value, priceChangePercent } = data.data[0];

          this.onTickerUpdate({
            baseVolume: size,
            change: normalizeNumber(Number(priceChangePercent)),
            close: lastPrice,
            high,
            last: Number(lastPrice).toFixed(this.precision),
            low,
            quoteVolume: value,
          });
        }

        if (channel === klineChannel && data?.data?.[0]) {
          const last = data.data[data.data.length - 1];
          const [time, open, high, low, close, volume] = last;

          this.klineUpdate([
            Math.floor(Number(time) / 1000),
            String(open),
            String(high),
            String(low),
            String(close),
            String(volume),
          ]);
        }

        if (channel === depthChannel && data?.data?.[0]) {
          const depthType = data.data[0].depthType as 'SNAPSHOT' | 'CHANGED';

          const responseAsks = toTuples(data.data[0].asks ?? []);
          const responseBids = toTuples(data.data[0].bids ?? []);

          const asks =
            depthType === 'SNAPSHOT'
              ? (mergeOrderBook(responseAsks, []) as [string, string][])
              : (mergeOrderBook(this.asks, responseAsks) as [string, string][]);

          const bids =
            depthType === 'SNAPSHOT'
              ? (mergeOrderBook(responseBids, []) as [string, string][])
              : (mergeOrderBook(this.bids, responseBids) as [string, string][]);

          this.hasWsDepthSnapshot = true;
          const nearest = keepNearestOrderbookLevels(asks, bids, WEEX_WS_DEPTH_LEVEL);
          this.asks = nearest.asks;
          this.bids = nearest.bids;

          this.publishOrderbook();
        }

        if (channel === tradesChannel && data?.data?.[0]) {
          for (const { time, price, size, buyerMaker } of data.data) {
            this.onTradeUpdate({
              price,
              quantity: size,
              timestamp: time,
              // buyerMaker true => buyer is the maker => the aggressor sold.
              side: buyerMaker ? 'sell' : 'buy',
            });
          }
        }
      } catch (error) {
        console.log('Weex socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    if (this.depthWs) {
      this.depthWs.close();
      this.depthWs = undefined;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
    this.hasWsDepthSnapshot = false;
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    // WEEX depth WS is a fixed 200-level book with no server-side scaling, so the tick
    // switch is a pure client-side re-aggregation.
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth(depth: number) {
    this.depth = depth;
    this.publishOrderbook();
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new WeexStrategy(args);

    await bootstrapStrategy(instance);

    return instance;
  };
}
